import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

/**
 * Message parser for pleased HTML messages. There are certain message TEXT types, like
 * 1. User Selections: The selections made by user before starting the chat.
 * 2. Chat Transcript: The transcript of the chats.
 * (P.S.: These are the only ones as of now, please add if there are more types for reference.)
 *
 * These parsers will check the text for any such messages and try to convert the message TEXT back to
 * the original version, i.e. with parsable information.
 *
 * NOTE: These parsers are shared by both FE & FE Chatbot repos. Any change should be reflected in both files.
 */
export interface ParsedMessage {
  version: 'v1' | 'v2';
  timestamp?: Date | null;
  authorName?: string;
  authorType?: 'AGENT' | 'CUSTOMER';
  message?: string;
  attachment?: { fileName: string } | null;
}

export interface ParsedMessageV2 extends ParsedMessage {
  version: 'v2';
  userSelection?: boolean;
  attachment?: { id: number; fileName: string } | null;
}

export interface PleasedHtmlMessageParser {
  /**
   * Parser version
   * `v2` syntax is backwards compatible with `v1` syntax. i.e. `v1` parser can parse `v2` syntax. This
   * might not be true for future versions.
   *
   * 1) v1: This is just HTML without properties we get from the server. It does not have much information on the
   *        message. We can currently get the `authorName`, `authorType`, `text` and `timestamp` from
   *        this parser. v1 parser has limitations like not getting the entire data or minimal support
   *        for attachments and timestamps.
   * 2) v2: The v2 syntax will get almost everything from html chat messages with precision. Faster and
   *        more accurate than `v1`. v2 syntax uses `data` properties, so that the message contents, i.e.
   *        the html can change in the future, but the data is still parsable as long as it has those
   *        data properties.
   *
   * 3) v5: TODO: Store messages as JSON data prop directly with security. Might do in later iterations.
   *
   *
   */
  readonly version: 'v1' | 'v2';
  /**
   * Lightly check if the source text conforms with the parser
   * version, without actually parsing. This is a quick check, this does not guarantee that
   * the parser will not yield any errors when actually parsing.
   * If this returns `false`, it is guaranteed that the parser _wont_ be
   * able to parse the source text.
   */
  conforms(source: string): boolean;
  /**
   * Parse the string and get a message object.
   * @param source The text to parse
   * @throws ParingError - Couldn't parse.
   */
  parse(source: string): ParsedMessage[];
  /**
   * Try parsing source and return messages if parsing was successful. if not, then
   * return null.
   * @param source The HTML source to parse
   */
  safeParse(source: string): ParsedMessage[] | null;
}

export class ParsingError extends Error {}

function safeParse(this: PleasedHtmlMessageParser, text: string) {
  try {
    return this.parse(text);
  } catch (e) {
    // eslint-disable-next-line no-console
    logger.error('[PLEASED_PARSER] Parsing error', { error: parseErrorMessage(e) });
    return null;
  }
}

function isHtml(text: string) {
  return /<(?=.*? .*?\/ ?>|br|hr|input|!--|wbr)[a-z]+.*?>|<([a-z]+).*?<\/\1>/i.test(text);
}

export class ParserAlpha implements PleasedHtmlMessageParser {
  protected static readonly sharedInstance = new ParserAlpha();
  static instance() {
    return this.sharedInstance;
  }

  readonly version = 'v1';
  readonly safeParse = safeParse;
  readonly passive = true;

  conforms(text: string): boolean {
    return Boolean(
      text &&
      text.trim() &&
      isHtml(text) &&
      text.trim().startsWith('<p') &&
      (text.includes('customer-message') || text.includes('agent-message'))
    );
  }

  tryParsingAttachmentName(p: HTMLParagraphElement) {
    if (!p.textContent?.includes('Attached a new file') || !p.querySelector('b')) return null;
    return p.querySelector('b')?.textContent?.trim() || null;
  }

  parse(source: string): ParsedMessage[] {
    const { version } = this;
    let nodes = Array.from(new DOMParser().parseFromString(source, 'text/html').querySelectorAll('p'));
    if (!nodes?.length) {
      throw new ParsingError('Either empty string is passed, or cannot get the paragraph nodes from string.');
    }

    if (this.passive) {
      nodes = nodes.filter((p) => p.querySelectorAll('label.customer-message, label.agent-message').length);
    }

    return nodes.map((p) => {
      const label = p.querySelector('label');
      if (!label) {
        throw new ParsingError('Cannot find the author label.');
      }
      p.removeChild(label);
      const isFromCustomer = Array.from(label.classList)
        .map((s) => s.trim())
        .includes('customer-message');
      const authorName = label.textContent?.trim() || '';
      const messageContent = p.textContent?.trim();
      const timestampText = messageContent?.match(/Timestamp:(.*)UTC/)?.[1].trim();
      const sanitizedTimestampText = timestampText?.endsWith('Z') ? timestampText : timestampText + 'Z';
      const timestamp =
        !sanitizedTimestampText || isNaN(Date.parse(sanitizedTimestampText + 'Z'))
          ? null
          : new Date(sanitizedTimestampText + 'Z');
      const message = messageContent?.replace(/\(Timestamp:(.*)UTC\)/, '').trim();

      const fileName = this.tryParsingAttachmentName(p);

      return {
        ...(fileName ? { attachment: { fileName } } : null),
        message,
        timestamp,
        version,
        authorType: !isFromCustomer ? 'AGENT' : 'CUSTOMER',
        authorName:
          authorName[authorName.length - 1] === ':' ? authorName.substring(0, authorName.length - 1) : authorName,
      };
    });
  }
}

const ParamResolve = {
  bool(value: string | boolean, defaultValue = false): boolean {
    if (!value) return defaultValue;
    if (typeof value === 'boolean') return value;
    const isTrue = `${value}`.toLocaleLowerCase().trim() === 'true';
    const isFalse = `${value}`.toLocaleLowerCase().trim() === 'false';
    if (isTrue) return true;
    if (isFalse) return false;
    return defaultValue;
  },
  integer(value: string | number, defaultValue = null): number | null {
    if (typeof value === 'number') return value;
    if (!value || isNaN(+value)) return defaultValue;
    return +value;
  },
};

/**
 * Parser V2 implementation. This implementation will fallback to ParserAlpha (V1) if individual elements of
 * the source cannot be parsed with V2 parser.
 */
export class ParserBeta implements PleasedHtmlMessageParser {
  protected static readonly sharedInstance = new ParserBeta();
  static instance() {
    return this.sharedInstance;
  }
  readonly version = 'v2';
  readonly safeParse = safeParse;
  protected readonly alpha = ParserAlpha.instance();

  parse(source: string): Array<ParsedMessage | ParsedMessageV2> {
    const nodes = Array.from(
      new DOMParser()
        .parseFromString(`<div id="parser">${source}</div>`, 'text/html')
        .querySelectorAll<HTMLElement>('#parser > *')
    );
    if (!nodes?.length) {
      throw new ParsingError('Either empty string is passed, or cannot get the paragraph nodes from string.');
    }

    const parsed: Array<ParsedMessage | ParsedMessageV2> = nodes.flatMap((node) => {
      const {
        plSyntax = null,
        attachmentName = '',
        authorName = '',
        attachmentId = '',
        authorType = 'CUSTOMER',
        timestamp: time = '',
        userSelection: userSelectionString = false,
      } = node.dataset;

      if (plSyntax !== this.version) {
        // Fallback to V1 parser
        return this.alpha.parse(node.outerHTML);
      }

      const messageNode = node.querySelector('[data-part="body"]');

      const message = messageNode?.textContent?.trim() || '';

      // clean
      const parsedAttachmentId = attachmentId && ParamResolve.integer(attachmentId);
      const attachment = parsedAttachmentId ? { id: parsedAttachmentId, fileName: attachmentName } : null;
      const userSelection = ParamResolve.bool(userSelectionString);
      const timestamp = isNaN(Date.parse(time)) ? null : new Date(time);

      return {
        authorName,
        message,
        authorType,
        attachment,
        userSelection,
        timestamp,
        version: this.version,
      } as ParsedMessageV2;
    });

    return parsed;
  }

  conforms(text: string): boolean {
    if (!isHtml(text)) return false;
    return text.includes("data-pl-syntax='v2'") || text.includes('data-pl-syntax="v2"');
  }
}

/**
 * Try parsing the given string and extracted all information that we can from the text content.
 * This will extract individual messages from the HTML which is commonly done for Chat Messages
 * and user selections. This will try to convert it back to a list of messages that can be
 * consumed by chatbot.
 *
 * Automatically checks if there are any parsers that match the spec in the source and safely
 * read the data.
 *
 * @param source The source to parse.
 * @returns Parsed messages.
 */
export function autoParse(source: string): Array<ParsedMessage | ParsedMessageV2> | null {
  const parsers = [ParserBeta.instance(), ParserAlpha.instance()];
  for (const parser of parsers) {
    if (parser.conforms(source)) {
      const messages = parser.safeParse(source);
      return messages?.length ? messages : null;
    }
  }
  return null;
}

/**
 * Check if the message object is a result of {@link ParserBeta} parsing, i.e. the message is
 * an interface of {@link ParsedMessageV2}. This is useful to check as `ParsedMessageV2` has more
 * information that the V1 parser.
 *
 * @param message The object to check
 * @returns True if the message is a result of v2 message.
 */
export function isParsedMessageV2(message: unknown): message is ParsedMessageV2 {
  return (message as ParsedMessageV2)?.version === 'v2';
}
